import { useApplicantAPI } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  Text,
  Modal,
  PrimaryButton,
  Stack,
} from "@certn/cascadia";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  RadioGroup,
  Radio,
  SectionMessage,
  TextArea,
  List,
  ListItem,
} from "@certn/cascadia-react";
import { JsonSchema } from "@jsonforms/core";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplicationFormContext } from "../../../ApplicationFormContext";
import { useNonBiometricIdOptOutHandler } from "../../one-id/hooks/useNonBiometricIdOptOutHandler";

type Props = {
  optOutChoices: JsonSchema[] | undefined;
  show: boolean;
  onClose: () => void;
};

type OptOutOptionsStateType = {
  otherIsSelected: boolean;
  optOutReason: string;
  optOutOtherReason: string;
};

export function NonBiometricIdOptOutModal(props: Props) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { optOutChoices, show, onClose } = props;
  const [optOutValues, setOptOutValues] = useState<OptOutOptionsStateType>({
    otherIsSelected: false,
    optOutReason: "",
    optOutOtherReason: "",
  });

  const changeNonBiometricIdOptOut = useNonBiometricIdOptOutHandler();

  const { data: checks } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/idv-check-list/`
  );

  const handleOptOut = async () => {
    await changeNonBiometricIdOptOut(
      optOutValues.optOutReason,
      optOutValues.optOutOtherReason
    );
  };

  if (!show) {
    return null;
  }

  return (
    <Modal isOpen={show} testID="non-biometric-opt-out-modal">
      <Dialog width={600} aria-label="non-biometric-modal-dialogue">
        {() => (
          <>
            <DialogHeader
              onClose={onClose}
              testID="non-biometric-opt-out-modal"
            >
              <Text style="global/modal-header" color="text-weak">
                <Trans>Unable to verify via my bank</Trans>
              </Text>
            </DialogHeader>
            <DialogContent>
              <Stack gap="space-4">
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    Select a reason for not completing the non-biometric
                    identity verification step. This information will be
                    displayed in the report.
                  </Trans>
                </Text>
                <RadioGroup
                  aria-label="non-biometric-opt-out-radio-group"
                  onChange={(value) => {
                    setOptOutValues((currValues) => ({
                      ...currValues,
                      optOutReason: value,
                      otherIsSelected: value === "OTHER",
                      optOutOtherReason:
                        value !== "OTHER" ? "" : optOutValues.optOutOtherReason,
                    }));
                  }}
                  isRequired
                  children={
                    optOutChoices &&
                    optOutChoices.map((item, index) => {
                      return (
                        <Radio
                          key={index}
                          value={item.const}
                          testID={`${item.const}-radio-selection`}
                        >
                          {item.title}
                        </Radio>
                      );
                    })
                  }
                  testID="non-bio-opt-out-reason-radio-group"
                />
                <TextArea
                  name="optOutOtherReason"
                  aria-label="non-bio-opt-out-reason-text-area"
                  onChange={(value) => {
                    setOptOutValues((currValues) => ({
                      ...currValues,
                      optOutOtherReason: value,
                    }));
                  }}
                  testID="non-bio-opt-out-reason-text-area"
                  maxLength={100}
                  isRequired={optOutValues.otherIsSelected}
                  isDisabled={!optOutValues.otherIsSelected}
                />
                <SectionMessage type="warning" icon="warning">
                  <Stack gap="space-4">
                    <Text style="global/heading-small">
                      <Trans>
                        By opting out of Non-Biometric Identity Verification, we
                        may not be able to complete the following services
                        included in your application:
                      </Trans>
                    </Text>
                    <List type="unordered">
                      {checks &&
                        checks?.non_bio_id_check_list &&
                        checks?.non_bio_id_check_list.map(
                          (checkName: string, checkIndex: number) => (
                            <ListItem key={checkIndex}>
                              <Text style="global/heading-small">
                                {checkName}
                              </Text>
                            </ListItem>
                          )
                        )}
                      <ListItem>
                        <Text style="global/heading-small">
                          {t("ID Verification")}
                        </Text>
                      </ListItem>
                    </List>
                  </Stack>
                </SectionMessage>
              </Stack>
            </DialogContent>
            <DialogFooter>
              <ButtonGroup>
                <BasicButton
                  onPress={() => {
                    setOptOutValues((currValues) => ({
                      ...currValues,
                      otherIsSelected: false,
                      optOutReason: "",
                      optOutOtherReason: "",
                    }));
                    onClose();
                  }}
                >
                  <Trans>Cancel</Trans>
                </BasicButton>
                <PrimaryButton
                  isDisabled={
                    !optOutValues.optOutReason ||
                    (optOutValues.optOutReason === "OTHER" &&
                      !optOutValues.optOutOtherReason)
                  }
                  onPress={() => {
                    handleOptOut();
                    onClose();
                  }}
                  size="medium"
                >
                  <Trans>Continue</Trans>
                </PrimaryButton>
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </Modal>
  );
}
